import * as THREE from 'three';
import { TICK_HZ } from '@somnhold/content';
import { monCovered, monJobAt, type BattleState, type MonasteryRules } from '@somnhold/sim';
import { simToWorld } from '@somnhold/render';
import { pinMark, remeasureMark } from './mark-pin.ts';
import { t } from '../i18n.ts';
import { h, setStyle } from '../ui/dom.ts';
import { ICONS } from '../ui/icons.ts';

/** A handful of salt: a low white heap, grains thrown off it. */
const SALT = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 12.5c1.4-3.2 3.6-5 6-5s4.6 1.8 6 5z" fill="currentColor"/><circle cx="4" cy="5" r="1" fill="currentColor"/><circle cx="7.5" cy="3.2" r="1" fill="currentColor"/><circle cx="11.6" cy="4.6" r="1" fill="currentColor"/></svg>';
/** A cell's door: an arch with its plank lines. */
const DOOR = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3.5 14.5V7a4.5 4.5 0 0 1 9 0v7.5z" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M8 3v11.5M5.8 8.5v6M10.2 8.5v6" stroke="currentColor" stroke-width="1" opacity="0.7"/></svg>';
/** The refectory's sack, tied at the neck. */
const SACK = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M5.5 4.2h5l-1 1.6c2.6 1.3 4 3.5 4 5.7 0 2-2.4 3-5.5 3s-5.5-1-5.5-3c0-2.2 1.4-4.4 4-5.7z" fill="currentColor"/><path d="M5 2.2l1.6 1.6M11 2.2L9.4 3.8" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>';
/** A sluice gate: a channel, the gate in it, its wheel. */
const GATE = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 13h12" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><rect x="5" y="6" width="6" height="5.5" rx="0.6" fill="currentColor"/><circle cx="8" cy="3" r="1.8" fill="none" stroke="currentColor" stroke-width="1.2"/></svg>';
/** The cistern: a round basin, water in it. */
const CISTERN = '<svg viewBox="0 0 16 16" aria-hidden="true"><ellipse cx="8" cy="5" rx="6" ry="2.2" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="M2 5v6c0 1.2 2.7 2.2 6 2.2s6-1 6-2.2V5" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="M2.6 8.5c1.6.8 3.4 1.2 5.4 1.2s3.8-.4 5.4-1.2V11c0 1-2.4 1.8-5.4 1.8S2.6 12 2.6 11z" fill="currentColor"/></svg>';
/** An altar's flame. */
const FLAME = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5c.6 2.6 3.6 4.2 3.6 7.6A3.6 3.6 0 0 1 8 12.7a3.6 3.6 0 0 1-3.6-3.6c0-1.7.9-2.7 1.7-3.6.2 1.2.7 1.9 1.4 2.2C7.2 5.7 7.4 3.5 8 1.5z" fill="currentColor"/><path d="M4 14.5h8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
/** A spectre: a hooded shape fading at its hem. */
const GHOST = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.8c-2.8 0-4.6 2.2-4.6 5V14l1.5-1.2 1.5 1.2L8 12.8l1.6 1.2 1.5-1.2 1.5 1.2V6.8c0-2.8-1.8-5-4.6-5z" fill="currentColor" opacity="0.85"/><circle cx="6.3" cy="6.8" r="0.9" fill="#0e0f13"/><circle cx="9.7" cy="6.8" r="0.9" fill="#0e0f13"/></svg>';
/** The great sentinel: a tall helm with its slit. */
const SENTINEL = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5l4.5 2.5V10c0 2.3-2 4-4.5 4.5C5.5 14 3.5 12.3 3.5 10V4z" fill="currentColor"/><path d="M5.5 7.2h5" stroke="#0e0f13" stroke-width="1.3" stroke-linecap="round"/></svg>';

/** How long Ser Edda's call stays up (ticks). */
const CALL_TICKS = 3 * TICK_HZ;
/** The siege's band shown as coming this long before it climbs the wall (ticks). */
const SIEGE_WARN = 10 * TICK_HZ;

type Mark = { el: HTMLButtonElement; state: HTMLElement; at: number };

/**
 * The monastery of the waterfall on screen (docs/sendas/paladin.md, chapter 4), inside a path's own plate
 * (game/senda-hud.ts) and over the board; a phone's plate kept under about a quarter of the screen:
 *
 * - the memory at the door (`porch`): whether the squire stands within Ser Edda's cover (blows land lighter) or away
 *   from her (the whole blow), and «¡Ser Edda te llama!» red, in the plate and over Edda, for a few seconds when she
 *   calls. The Marea's clock is the plate's own goal line; Edda's life isn't shown (a memory: she doesn't fall).
 * - the cells (`cells`): the handfuls of salt left (none: to the refectory), the fifth altar (lit, or out and Marit
 *   lighting it), the spectres' next group. Over the board, a mark on each salt spot (salted reads white and full),
 *   each cell's door (the shut one reads red) and the refectory's sacks, tappable: the hero goes and does it.
 * - the cloister (`cloister`, the test): the cistern's twelve marks with the bare line at two; the three gates (open,
 *   shut, full, and what went out of each); the fifth altar's snuffs and, red, «la próxima apaga el quinto altar»; the
 *   great sentinel (when it comes up, up, down) and the siege (coming, on the patio, quiet). A mark on each gate.
 *
 * Plain DOM, written only when it changes (docs/RENDIMIENTO.md); the board's points from the sim's own layout.
 */
export class MonasteryHud {
  readonly panel: HTMLElement;
  private kind: MonasteryRules['kind'];
  private v = new THREE.Vector3();
  private marks: Mark[] = [];
  /** The marks' world points (fixed: the board doesn't move). */
  private points: THREE.Vector3[] = [];
  private key = '';
  // The memory.
  private coverRow = h('div.bh-row.mn-cover');
  private cover = h('span.al-wave');
  private call = h('div.mn-call', {}, t('senda.mon.call'));
  private callUntil = -1;
  private callW = 0;
  // The cells.
  private saltRow = h('div.bh-row.mn-salt');
  private saltPips: HTMLElement[] = [];
  private saltText = h('span.al-wave');
  private altarRow = h('div.bh-row.al-waverow.mn-altar');
  private altarText = h('span.al-wave');
  private ghostRow = h('div.bh-row.mn-ghosts');
  private ghostText = h('span.al-wave');
  // The cloister.
  private poolRow = h('div.bh-row.mn-pool');
  private poolPips: HTMLElement[] = [];
  private poolText = h('span.al-wave');
  private gatesRow = h('div.bh-row.mn-gates');
  private gateChips: { el: HTMLElement; n: HTMLElement }[] = [];
  private snuffRow = h('div.bh-row.al-waverow.mn-snuff');
  private snuffText = h('span.al-wave');
  private duoRow = h('div.bh-row.mn-duo');
  private boss = h('span.mn-half');
  private bossText = h('b');
  private siege = h('span.mn-half');
  private siegeText = h('b');

  constructor(
    s: BattleState,
    private board: HTMLElement,
    private mark: (at: number) => void,
  ) {
    const r = s.rules?.senda?.monastery;
    this.kind = r?.kind ?? 'porch';
    const rows: HTMLElement[] = [];
    if (r?.porch) {
      this.coverRow.append(h('span.bh-ico', { html: ICONS.shield }), h('span.bh-label', {}, t('senda.mon.edda')), this.cover);
      rows.push(this.coverRow);
      this.board.append(this.call);
    }
    const C = r?.cells;
    if (r && C) {
      this.saltPips = Array.from({ length: C.handfuls }, () => h('i'));
      this.saltRow.append(h('span.bh-ico', { html: SALT }), h('span.bh-label', {}, t('senda.mon.salt')), h('span.al-pips.mn-grains', {}, ...this.saltPips), this.saltText);
      this.altarRow.append(h('span.bh-ico', { html: FLAME }), h('span.bh-label', {}, t('senda.mon.altar')), this.altarText);
      this.ghostRow.append(h('span.bh-ico', { html: GHOST }), h('span.bh-label', {}, t('senda.mon.spectres')), this.ghostText);
      rows.push(this.saltRow, this.altarRow, this.ghostRow);
      // The spots (0..8), the doors (9..16), the sacks (17).
      const n = C.spots.length + C.doors.length + 1;
      for (let at = 0; at < n; at++) {
        const spot = at < C.spots.length;
        const sack = at === n - 1;
        const d = at - C.spots.length;
        const cls = spot ? '.mn-spot' : sack ? '.mn-sack' : '.mn-door';
        const test = spot ? `mon-spot-${at}` : sack ? 'mon-sack' : `mon-door-${d}`;
        const hint = spot ? t('senda.mon.spotHint') : sack ? t('senda.mon.fetch') : t('senda.mon.doorHint');
        this.addMark(at, cls, test, hint, spot ? SALT : sack ? SACK : DOOR, sack ? t('senda.mon.sack') : '');
        // A door's mark on the door itself (on the wall), where it reads as that cell's; the hero still goes to its line.
        const p = !spot && !sack ? C.doors[d] : monJobAt(r, at);
        this.points.push(p ? simToWorld(p.x, p.y, new THREE.Vector3(), 0.05) : new THREE.Vector3());
      }
    }
    const K = r?.cloister;
    if (r && K) {
      this.poolPips = Array.from({ length: K.cap }, (_, i) => h(i === K.bare - 1 ? 'i.bare' : 'i'));
      this.poolRow.append(h('span.bh-ico', { html: CISTERN }), h('span.bh-label', {}, t('senda.mon.cistern')), h('span.al-pips.mn-water', { title: t('senda.mon.waterfall') }, ...this.poolPips), this.poolText);
      const names = ['senda.mon.orchards', 'senda.mon.kitchen', 'senda.mon.ravine'];
      this.gateChips = names.map((k) => {
        const n = h('small');
        const el = h('span.mn-chip', {}, h('b', {}, t(k)), n);
        return { el, n };
      });
      this.gatesRow.append(h('span.bh-ico', { html: GATE }), h('span.mn-chips', {}, ...this.gateChips.map((c) => c.el)));
      this.snuffRow.append(h('span.bh-ico', { html: FLAME }), h('span.bh-label', {}, t('senda.mon.altar')), this.snuffText);
      this.boss.append(h('span.bh-ico', { html: SENTINEL }), h('span', {}, t('senda.mon.sentinelShort')), this.bossText);
      this.siege.append(h('span.bh-ico', { html: ICONS.skull }), h('span', {}, t('senda.mon.siege')), this.siegeText);
      this.duoRow.append(this.boss, this.siege);
      rows.push(this.poolRow, this.gatesRow, this.snuffRow, this.duoRow);
      const hints = ['senda.mon.orchardsHint', 'senda.mon.kitchenHint', 'senda.mon.ravineHint'];
      K.gates.forEach((g, i) => {
        this.addMark(i, `.mn-gate.g${i}`, `mon-gate-${i}`, t(hints[i] as string), GATE, t(names[i] as string));
        this.points.push(simToWorld(g.x, g.y, new THREE.Vector3(), 0.05));
      });
    }
    this.panel = h(`div.bh.mn-panel.mn-${this.kind}`, {}, ...rows);
  }

  private addMark(at: number, cls: string, test: string, hint: string, glyph: string, name: string): void {
    const state = h('small.al-state');
    const el = h(
      `button.al-stone.mn-mark${cls}`,
      { type: 'button', 'data-test': test, 'aria-label': hint, title: hint, onpointerup: (e: Event) => (e.stopPropagation(), this.mark(at)) },
      h('span.al-ring'),
      h('span.al-glyph', { html: glyph }),
      name ? h('b.mn-name', {}, name) : '',
      state,
    ) as HTMLButtonElement;
    this.board.append(el);
    this.marks.push({ el, state, at });
  }

  /** A mark's state under its ring; its box (kept off the name tags) read again when the words change. */
  private setState(m: Mark, text: string): void {
    if (m.state.textContent === text) return;
    m.state.textContent = text;
    remeasureMark(m.el);
  }

  update(s: BattleState): void {
    const r = s.rules?.senda?.monastery;
    const w = s.senda?.monastery;
    if (!r || !w) return;
    const secs = (ticks: number): number => Math.max(1, Math.ceil(ticks / TICK_HZ));
    if (r.porch) {
      const hero = s.heroes[0];
      const by = !!hero && monCovered(s, hero);
      for (const e of s._events ?? []) if (e.t === 'senda' && e.kind === 'call') this.callUntil = s.tick + CALL_TICKS;
      const calling = this.callUntil > s.tick;
      const text = calling ? t('senda.mon.call') : by ? t('senda.mon.beside') : t('senda.mon.alone');
      if (this.cover.textContent !== text) this.cover.textContent = text;
      this.coverRow.classList.toggle('by', by && !calling);
      this.coverRow.classList.toggle('calling', calling);
      this.call.classList.toggle('on', calling);
    }
    const C = r.cells;
    if (C) {
      const key = `${w.salt}|${w.salted.map((x) => (x ? 1 : 0)).join('')}|${w.shut}`;
      if (key !== this.key) {
        this.key = key;
        this.saltPips.forEach((p, i) => p.classList.toggle('on', i < w.salt));
        const st = w.salt > 0 ? t('senda.mon.handfuls', { n: w.salt, of: C.handfuls }) : t('senda.mon.fetch');
        this.saltText.textContent = st;
        this.saltRow.classList.toggle('empty', w.salt <= 0);
        for (const m of this.marks) {
          if (m.at < C.spots.length) {
            const salted = !!w.salted[m.at];
            m.el.classList.toggle('salted', salted);
            m.el.classList.toggle('dry', !salted && w.salt <= 0);
            this.setState(m, '');
          } else if (m.at < C.spots.length + C.doors.length) {
            const shut = w.shut === m.at - C.spots.length;
            m.el.classList.toggle('shut', shut);
            this.setState(m, shut ? t('senda.mon.shut') : '');
          } else m.el.classList.toggle('want', w.salt <= 0);
        }
      }
      const out = w.outAt >= 0;
      const altar = out ? t('senda.mon.out') : w.outs > 0 ? `${t('senda.mon.lit')} · ${t('senda.mon.outs', { n: w.outs })}` : t('senda.mon.lit');
      if (this.altarText.textContent !== altar) this.altarText.textContent = altar;
      this.altarRow.classList.toggle('warn', out);
      const next = C.first + w.band * C.every;
      const ghosts = t('senda.mon.next', { s: secs(next - s.waveT) });
      if (this.ghostText.textContent !== ghosts) this.ghostText.textContent = ghosts;
    }
    const K = r.cloister;
    if (K) {
      const kitchenNext = w.out[1]! + 1;
      const warnKitchen = kitchenNext >= K.snuffAt && (kitchenNext - K.snuffAt) % K.snuffEvery === 0 && w.snuffs < K.snuffs;
      const key = `${w.level}|${w.open.join('')}|${w.out.join(',')}|${w.snuffs}|${w.bossAt >= 0 ? 1 : 0}|${w.bossId > 0 ? 1 : w.bossId}`;
      if (key !== this.key) {
        this.key = key;
        this.poolPips.forEach((p, i) => p.classList.toggle('on', i < w.level));
        this.gateChips.forEach((c, g) => {
          const full = g === 0 && w.out[0]! >= K.orchards;
          const open = w.open[g] === 1;
          c.el.classList.toggle('open', open);
          c.el.classList.toggle('full', full);
          c.el.classList.toggle('warn', g === 1 && open && warnKitchen);
          c.n.textContent = g === 0 ? `${w.out[0]}/${K.orchards}` : String(w.out[g]);
          c.el.title = g === 1 ? t('senda.mon.kitchenOut', { n: w.out[1] ?? 0 }) : '';
        });
        for (const m of this.marks) {
          const g = m.at;
          const full = g === 0 && w.out[0]! >= K.orchards;
          const open = w.open[g] === 1;
          const warn = (g === 1 && warnKitchen) || (g === 2 && !open && w.bossAt < 0 && w.bossId === 0);
          m.el.classList.toggle('open', open);
          m.el.classList.toggle('full', full);
          m.el.classList.toggle('warn', warn);
          this.setState(m, full ? t('senda.mon.full') : g === 1 && warnKitchen ? t('senda.mon.snuffMark') : g === 2 && warn ? t('senda.mon.ravineMark') : open ? t('senda.mon.open') : t('senda.mon.closed'));
        }
        const snuffs = t('senda.mon.snuffs', { n: w.snuffs, of: K.snuffs });
        const snuffText = warnKitchen ? `${snuffs} · ${t('senda.mon.snuffNext')}` : snuffs;
        if (this.snuffText.textContent !== snuffText) this.snuffText.textContent = snuffText;
        this.snuffRow.classList.toggle('warn', warnKitchen && w.open[1] === 1);
        this.snuffRow.classList.toggle('lost', !warnKitchen && w.snuffs > 0);
        // (Nothing to say while the kitchen's water is far from the chapel: a phone's plate stays short.)
        this.snuffRow.hidden = !warnKitchen && w.snuffs === 0;
      }
      const bare = w.level <= K.bare;
      const pool = w.lit ? t('senda.mon.sixthLit') : bare ? (w.lightHeld > 0 ? t('senda.mon.lighting') : t('senda.mon.bare')) : t('senda.mon.marks', { n: w.level, of: K.cap });
      if (this.poolText.textContent !== pool) this.poolText.textContent = pool;
      this.poolRow.classList.toggle('bare', bare);
      this.poolRow.classList.toggle('lit', w.lit);
      // The great sentinel: when it comes up (the ravine's call or the late hour), up, down.
      const at = w.bossAt >= 0 ? w.bossAt - s.tick : K.boss.late - s.waveT;
      const boss = w.bossId > 0 ? t('senda.mon.sentinelUp') : w.bossId < 0 ? t('senda.mon.sentinelDown') : t('senda.mon.sentinelIn', { s: secs(at) });
      if (this.bossText.textContent !== boss) this.bossText.textContent = boss;
      this.boss.classList.toggle('warn', w.bossId > 0 || (w.bossAt >= 0 && w.bossId === 0));
      this.boss.classList.toggle('done', w.bossId < 0);
      // The siege: on the patio (any of it standing), coming (its band within ten seconds), quiet.
      const up = s.enemies.some((e) => e.hp > 0 && e.id !== w.bossId);
      const nextBand = w.band < K.siege.at.length ? (K.siege.at[w.band] as number) - s.waveT : -1;
      const siege = up ? t('senda.mon.siegeOn') : nextBand >= 0 && nextBand <= SIEGE_WARN ? t('senda.mon.siegeIn', { s: secs(nextBand) }) : t('senda.mon.siegeQuiet');
      if (this.siegeText.textContent !== siege) this.siegeText.textContent = siege;
      this.siege.classList.toggle('warn', up || (nextBand >= 0 && nextBand <= SIEGE_WARN));
    }
    // The job the hero was sent to: its ring filling as it works.
    const running = s.result === 'running';
    const need = (at: number): number => (C && at === C.spots.length + C.doors.length ? C.fetch : r.turn);
    for (const m of this.marks) {
      const sent = w.job === m.at;
      m.el.classList.toggle('sent', sent);
      m.el.style.setProperty('--k', sent ? Math.min(1, w.held / Math.max(1, need(m.at))).toFixed(2) : '0');
      m.el.disabled = !running;
    }
  }

  /** Every frame: the marks over their places; Ser Edda's call over her. */
  place(camera: THREE.Camera, w: number, ht: number, at: Map<string, THREE.Vector3>): void {
    this.marks.forEach((m, i) => {
      const p = this.points[i];
      if (!p) return setStyle(m.el, 'display', 'none');
      this.v.copy(p).project(camera);
      if (this.v.z > 1 || this.v.x < -1.1 || this.v.x > 1.1 || this.v.y < -1.1 || this.v.y > 1.1) return setStyle(m.el, 'display', 'none');
      setStyle(m.el, 'display', '');
      // (Held a little inside the screen's sides: a mark's words at the edge would be cut.)
      pinMark(m.el, Math.min(w - 52, Math.max(52, (this.v.x * 0.5 + 0.5) * w)), (-this.v.y * 0.5 + 0.5) * ht);
    });
    if (this.kind !== 'porch') return;
    const e = at.get('edda');
    if (!e || !this.call.classList.contains('on')) return setStyle(this.call, 'display', 'none');
    this.v.copy(e).setY(e.y + 2.1).project(camera);
    if (this.v.z > 1) return setStyle(this.call, 'display', 'none');
    setStyle(this.call, 'display', '');
    // (Kept whole on the screen: Edda stands near its side on a phone. Its width read once.)
    if (!this.callW) this.callW = this.call.offsetWidth;
    const half = this.callW / 2 + 8;
    const x = Math.min(w - half, Math.max(half, (this.v.x * 0.5 + 0.5) * w));
    setStyle(this.call, 'transform', `translate(${x.toFixed(1)}px, ${((-this.v.y * 0.5 + 0.5) * ht).toFixed(1)}px) translate(-50%, -100%)`);
  }
}
